import type { HydraulicSystem, Scenarios } from '@shared/hydraulic'
import { getDescendantIds, DEFAULT_BASE_SCENARIO_ID } from '@shared/hydraulic'
import type { CalculationOutput } from '../../../services/calc-service'

export type ScenarioStatus = 'never' | 'stale' | 'fresh'
export interface ScenarioResult { output: CalculationOutput; materializedSystem: HydraulicSystem }
export type ScenarioResultsMap = Record<string, ScenarioResult>
export type ScenarioStatusMap = Record<string, ScenarioStatus>
/**
 * `failed` holds scenario NAMES for the compact inline summary. `failureDetails` holds the
 * matching `"<name>: <reason>"` strings — ⚠ previously the reason was logged and then DISCARDED,
 * so a scenario that failed for a specific, actionable engine reason (e.g. a SEAM_UNSUPPORTED
 * message naming the offending valve and the fix) surfaced as a bare name with no way to find out
 * why. Kept as a parallel list rather than changing `failed`'s shape so the inline summary stays
 * short; the detail is surfaced on hover.
 */
export interface ScenarioCalcProgress { total: number; done: number; failed: string[]; failureDetails?: string[] }

/**
 * Does this document carry solver results that are on screen right now?
 *
 * Two conditions, both required:
 *  - `calculation_result.status` is USABLE (`success`/`partial`). Presence alone is not
 *    enough: the schema admits `failed`/`invalid_*`, and a legacy or direct-API document
 *    can carry one. Those runs installed no numbers.
 *  - at least one element carries a `calc` block, i.e. there is something to display.
 *
 * Neither half is sufficient on its own.
 *  - Status alone is a false positive: `applyCalcResults` stamps `partial` even when the
 *    run produced no element results at all, so the badge would read "up to date" over
 *    nothing. The server's model PUT also accepts caller-supplied systems without checking
 *    that blocks and metadata agree, so the pair can be made to drift.
 *  - Blocks alone is a false positive in the direction that matters more: the Desktop
 *    importer SYNTHESISES `vertex.calc` out of Desktop's top-level pressure fields
 *    (`normalizeCalcFields`), so a never-calculated import would claim to have been solved.
 *    Requiring the status closes that hole, because the importer never writes
 *    `calculation_result`.
 *
 * KNOWN GAP (accepted): genuine blocks with NO `calculation_result` read as "not
 * calculated". That needs a legacy or direct-API document — no Connect writer produces
 * one — and the only fix is to fall back to block-presence, which reopens the Desktop
 * import hole above.
 */
export function hasPersistedResults(system: HydraulicSystem | null | undefined): boolean {
  if (!system) return false
  const status = system.calculation_result?.status
  if (status !== 'success' && status !== 'partial') return false
  return system.edges.some((e) => e.calc !== undefined)
    || system.vertices.some((v) => v.calc !== undefined)
}

/**
 * Strip solver-produced `calc` fields from every vertex/edge and clear
 * `calculation_result` from the system. Returns the SAME reference when there is
 * nothing to strip, so a no-op never churns referential equality for memoized
 * selectors or the save path's `liveSystem === savedSystem` check.
 */
export function clearCalcResults(system: HydraulicSystem): HydraulicSystem {
  const carries = system.calculation_result !== undefined
    || system.edges.some((e) => e.calc !== undefined)
    || system.vertices.some((v) => v.calc !== undefined)
  if (!carries) return system
  return {
    ...system,
    calculation_result: undefined,
    vertices: system.vertices.map((v) => ({ ...v, calc: undefined })),
    edges: system.edges.map((e) => ({ ...e, calc: undefined })),
  }
}

/**
 * The document keys the solver actually reads. A change to any of them invalidates the
 * persisted results; a change to anything else (selection, layout, labels, groups,
 * annotations, design constraints, unit display) does not.
 *
 * ⚠️ `vertices`/`edges` alone is NOT enough. Halving `max_iterations`, editing a referenced
 * fluid's density, or changing a pipe specification's roughness leaves both arrays
 * untouched while making every displayed number wrong — PIPE-66 surviving for the solver
 * inputs that do not live on an element (PIPE-77).
 *
 * ⚠️ `heat_transfer_settings` is included even though no store action mutates it in
 * isolation today (its only writer, `insertLibrarySnapshot`, rebuilds `edges` too and so
 * already clears). It describes thermal insulation layers and ambient conditions for pipe
 * heat loss — a physical input in kind. Omitting it would silently reintroduce this exact
 * bug class the day a CRUD action is added, and the two failure directions are not
 * symmetric: clearing too eagerly costs a recalculation, keeping too eagerly shows wrong
 * numbers.
 *
 * These are a subset of the keys the backend classifies as `'structural'` (as opposed to
 * the single `'derived'` key, `calculation_result`) in
 * `backend/src/features/workspace/model-diff.ts`, so the frontend now agrees with the
 * backend about what a solver input is rather than diverging from it.
 */
const SOLVER_INPUT_KEYS = [
  'vertices',
  'edges',
  'fluid_zones',
  'pipe_specifications',
  'calculation_settings',
  'heat_transfer_settings',
] as const satisfies ReadonlyArray<keyof HydraulicSystem>

/**
 * Strip the persisted results iff this edit changed BASE's solver inputs.
 *
 * ⚠️ The persisted `calc` blocks and `calculation_result` describe BASE and only Base —
 * `calculate()` installs them onto the document solely when Base is the active scenario,
 * and `usePipeResult` suppresses them off-Base for the same reason. So the question that
 * decides whether they survive an edit is "did Base's solver inputs change?". An
 * override-only edit rebuilds `scenarios` and nothing else, so Base's results stay valid
 * and MUST be kept.
 *
 * ⚠️ This replaced `if (get().calculationResults)`. That guard asked a SESSION variable
 * whether the DOCUMENT has results, and after a page reload the answer is a wrong "no":
 * `calculationResults` is null while the blocks are sitting on the model, so the edit
 * left stale numbers on screen and saved them back (PIPE-66).
 *
 * ⚠️ Not `invalidatedIds.includes('base')` either. A topology edit made while a CHILD
 * scenario is active writes Base data (`routeFieldEdit` sends every non-overridable field
 * to Base) but marks only the child's subtree stale, so Base would never appear in
 * `invalidatedIds` and its now-wrong blocks would survive.
 *
 * KNOWN LIMITATION (deliberate): this is reference equality per key, not a field-level
 * diff, so an edit that rebuilds `vertices` without touching a solver-relevant field —
 * renaming a junction is the everyday case — still clears. That is exactly what the old
 * guard did in-session, so this propagates existing behaviour to the reload path rather
 * than introducing new over-eagerness. Narrowing it means diffing field by field or
 * projecting the solver-relevant subset of every element, and `name` would not be the only
 * non-solver field to chase.
 */
export function clearCalcResultsIfSolverInputsChanged(
  before: HydraulicSystem,
  after: HydraulicSystem,
): HydraulicSystem {
  if (SOLVER_INPUT_KEYS.every((k) => before[k] === after[k])) return after
  return clearCalcResults(after)
}

/**
 * Mark staleness after an edit. Base edit -> all stale; child edit -> that node + descendants.
 * A scenario whose current status is 'never' (or absent -> coalesced to 'never') STAYS 'never'
 * (a never-calc'd scenario cannot become "stale"; without this a fresh model would flip every
 * node stale on the first edit). Returns the new status map + the ids whose result cache is now stale.
 */
export function markScenariosStale(
  scenarios: Scenarios,
  status: ScenarioStatusMap,
  editedScenarioId: string,
): { status: ScenarioStatusMap; invalidatedIds: string[] } {
  const next: ScenarioStatusMap = { ...status }
  const affected = editedScenarioId === DEFAULT_BASE_SCENARIO_ID
    ? scenarios.nodes.map((n) => n.id)
    : [editedScenarioId, ...getDescendantIds(scenarios.nodes, editedScenarioId)]
  const invalidatedIds: string[] = []
  for (const id of affected) {
    const cur = next[id] ?? 'never'
    if (cur === 'never') continue
    next[id] = 'stale'
    invalidatedIds.push(id)
  }
  return { status: next, invalidatedIds }
}

/**
 * Re-project the plain calc fields from the active scenario's per-scenario state.
 * When `scenarioResults[active]` is absent we PRESERVE the currently-stored
 * `calculationResults` (`current`), NOT force null — existing code/tests seed
 * `calculationResults` directly without a `scenarioResults` entry, and clobbering
 * it to null would stop the auto-calc guard from firing.
 */
export function projectActiveScenario(state: {
  scenarios?: Scenarios
  scenarioResults: ScenarioResultsMap
  scenarioStatus: ScenarioStatusMap
  current: CalculationOutput | null
}): { calculationResults: CalculationOutput | null; resultsStale: boolean } {
  const activeId = state.scenarios?.active_id ?? DEFAULT_BASE_SCENARIO_ID
  const result = state.scenarioResults[activeId]
  const status = state.scenarioStatus[activeId] ?? 'never'
  return {
    calculationResults: result ? result.output : state.current,
    resultsStale: status === 'stale',
  }
}

/**
 * Store-level helper used by every edit action: mark per-scenario staleness for the
 * active scenario, INVALIDATE the stale scenarios' result caches (delete their
 * `scenarioResults` entries so a cached `materializedSystem` is never stale), and
 * re-project the plain calc fields. Reproduces today's Base-active behavior exactly.
 *
 * ⚠️ `editedScenarioId` overrides the default "the active scenario was edited". Pass
 * `DEFAULT_BASE_SCENARIO_ID` for an edit to MODEL-WIDE input — a fluid, a pipe
 * specification, the calculation settings. Those belong to no single scenario, so
 * attributing them to whichever node happened to be active invalidated only that
 * subtree while the persisted blocks were cleared for the whole document: Base was left
 * 'fresh' over a cached output that no longer matched the model, and switching to it
 * reprojected those results as current. Base is the right id because
 * `markScenariosStale` already cascades a Base edit to every node, and because
 * `routeFieldEdit` routes every other non-overridable field to Base for the same reason.
 */
export function staleAfterEdit(args: {
  updatedSystem: HydraulicSystem
  scenarioResults: ScenarioResultsMap
  scenarioStatus: ScenarioStatusMap
  currentResults: CalculationOutput | null
  editedScenarioId?: string
}): { scenarioResults: ScenarioResultsMap; scenarioStatus: ScenarioStatusMap; calculationResults: CalculationOutput | null; resultsStale: boolean } {
  const scenarios = args.updatedSystem.scenarios
  if (!scenarios) {
    return { scenarioResults: args.scenarioResults, scenarioStatus: args.scenarioStatus,
      calculationResults: args.currentResults, resultsStale: false }
  }
  const editedId = args.editedScenarioId ?? scenarios.active_id
  const { status, invalidatedIds } = markScenariosStale(scenarios, args.scenarioStatus, editedId)
  const scenarioResults = { ...args.scenarioResults }
  for (const id of invalidatedIds) delete scenarioResults[id]
  const projection = projectActiveScenario({ scenarios, scenarioResults, scenarioStatus: status, current: args.currentResults })
  return { scenarioResults, scenarioStatus: status, ...projection }
}
